"use client";

import React from "react";
import Link from "next/link";
import { ToolInfo } from "@/contexts/ToolsContext";
import { getDashboardToolImages, getTotalStyleCount } from "@/components/dashboard/toolDashboardImageConfig";
import { trackFunnel } from "@/libs/analytics";
import { getToolHref } from "./jtbdData";

// ── Helpers ──

function fireClick(
  tool_id: string,
  card_type: "hero" | "style" | "background" | "outfit" | "tool" | "look",
  card_id: string,
  section: string,
  destination_href: string
) {
  trackFunnel("tool_engagement", "dashboard_card_clicked", {
    tool_id,
    card_type,
    card_id,
    section,
    destination_href,
  });
}

// ── Free Badge (green, shown on free tools only) ──

function FreeBadge() {
  return (
    <div className="absolute top-3 right-3 z-10 bg-[#69C85A] backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg pointer-events-none">
      <span className="text-sm font-bold text-white">Free</span>
    </div>
  );
}

// ── Style Card (avatar style, background, or outfit) ──

interface StyleCardProps {
  title: string;
  imageSrc: string;
  href: string;
  section: string;
  cardType: "style" | "background" | "outfit";
  cardId: string;
  toolId: string;
  /** Image box aspect ratio. Use portrait "3/4" for people/outfits, "4/3" for scenery/backgrounds. */
  aspect?: "4/3" | "3/4" | "1/1";
}

export function StyleCard({ title, imageSrc, href, section, cardType, cardId, toolId, aspect = "4/3" }: StyleCardProps) {
  return (
    <Link
      href={href}
      onClick={() => fireClick(toolId, cardType, cardId, section, href)}
      className="group block bg-white rounded-xl overflow-hidden hover:border-primary hover:shadow-lg transition-all border border-gray-100"
    >
      <div className={`relative w-full aspect-[${aspect}] bg-gray-100 overflow-hidden`}>
        <img
          src={imageSrc}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </div>
      <div className="p-2 md:p-3">
        <p className="font-semibold text-gray-800 text-xs md:text-sm leading-tight line-clamp-2">{title}</p>
      </div>
    </Link>
  );
}

// ── Look Card (curated headshot look: outfit + background + expression + pose) ──

interface LookCardProps {
  title: string;
  imageSrc: string;
  href: string;
  section: string;
  lookId: string;
  badge?: string;
}

export function LookCard({ title, imageSrc, href, section, lookId, badge }: LookCardProps) {
  return (
    <Link
      href={href}
      onClick={() => fireClick("ai-headshots", "look", lookId, section, href)}
      className="group block bg-white rounded-xl overflow-hidden hover:border-primary hover:shadow-lg transition-all border border-gray-100"
    >
      <div className="relative w-full aspect-[3/4] bg-gray-100 overflow-hidden">
        <img
          src={imageSrc}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-3 pt-8 pointer-events-none">
          <p className="font-semibold text-white text-sm leading-tight line-clamp-2">{title}</p>
        </div>
      </div>
    </Link>
  );
}

// ── Tool Card (with optional mini-thumbs strip) ──

interface ToolCardProps {
  tool: ToolInfo;
  section: string;
}

export function ToolCard({ tool, section }: ToolCardProps) {
  const images = getDashboardToolImages(tool);
  const totalStyles = getTotalStyleCount(tool.id);
  const thumbs = images.slice(1, 5);
  const remaining = totalStyles ? totalStyles - thumbs.length - 1 : 0;
  const href = getToolHref(tool);

  return (
    <Link
      href={href}
      onClick={() => fireClick(tool.id, "tool", tool.id, section, href)}
      className="group block bg-white rounded-xl overflow-hidden hover:border-primary hover:shadow-lg transition-all border border-gray-100"
    >
      <div className="relative w-full aspect-[4/3] bg-gray-100 overflow-hidden">
        <img
          src={images[0]}
          alt={tool.title}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        {thumbs.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1 p-2">
            {thumbs.map((src, i) => (
              <div
                key={i}
                className="w-10 h-10 md:w-12 md:h-12 rounded-lg overflow-hidden border-2 border-white shadow-sm bg-gray-200 shrink-0"
              >
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
            {remaining > 0 && (
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg border-2 border-white shadow-sm bg-black/60 flex items-center justify-center shrink-0">
                <span className="text-white text-xs font-bold">+{remaining}</span>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="p-3 md:p-4">
        <h3 className="font-bold text-gray-900 text-sm md:text-base leading-tight">{tool.title}</h3>
        <p className="text-xs md:text-sm text-gray-500 mt-1 line-clamp-2">{tool.description}</p>
      </div>
    </Link>
  );
}

// ── Compact Tool Card (horizontal / landscape) ──

interface CompactToolCardProps {
  tool: ToolInfo;
  section: string;
}

export function CompactToolCard({ tool, section }: CompactToolCardProps) {
  const href = getToolHref(tool);
  const isFree = tool.creditCost === 0;

  return (
    <Link
      href={href}
      onClick={() => fireClick(tool.id, "tool", tool.id, section, href)}
      className="group flex items-center gap-3 md:gap-4 p-3 rounded-xl bg-white border-4 border-gray-100 hover:border-primary hover:shadow-md transition-all"
    >
      <div className="w-20 h-20 md:w-20 md:h-20 rounded-xl overflow-hidden bg-gray-100 shrink-0">
        <img
          src={tool.imageUrl}
          alt={tool.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">{tool.title}</h3>
          {isFree && (
            <div className="shrink-0 bg-[#69C85A] backdrop-blur-sm rounded-lg px-2 py-0 pb-1 shadow-sm">
              <span className="text-xs font-bold text-white">FREE</span>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{tool.description}</p>
      </div>
    </Link>
  );
}

// ── Free Composite Card (CSS-layered: demo portrait + background/gradient/border/text) ──

export interface FreeCompositeSpec {
  kind: "background" | "gradient" | "border" | "text" | "shadow";
  /** Background image URL (kind=background) */
  backgroundUrl?: string;
  /** CSS background value, e.g. linear-gradient(...) (kind=gradient) */
  gradientCss?: string;
  /** Border ring color (kind=border) */
  borderColor?: string;
  /** Background inside the border circle (kind=border) — CSS color or gradient */
  borderBg?: string;
  /** Overlay text (kind=text) */
  text?: string;
  /** Overlay text color (kind=text) */
  textColor?: string;
  /** Shadow PNG URL layered behind the portrait (kind=shadow) */
  shadowUrl?: string;
}

interface FreeCompositeCardProps {
  title: string;
  spec: FreeCompositeSpec;
  href: string;
  section: string;
  cardId: string;
  toolId: string;
}

// Demo portraits from the free webapp (mixed across cards for variety).
const DEMO_PHOTOS = [
  "https://editor-embedded.pfpmaker.com/demo/demo1.png",
  "https://editor-embedded.pfpmaker.com/demo/demo2.png",
  "https://editor-embedded.pfpmaker.com/demo/demo3.png",
];

/** Stable per-card portrait: hash the cardId → pick a demo photo (deterministic, no flicker). */
function demoPhotoFor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = (h * 31 + key.charCodeAt(i)) | 0;
  }
  return DEMO_PHOTOS[Math.abs(h) % DEMO_PHOTOS.length];
}

/**
 * Full-bleed composite: the demo portrait FILLS the card; the treatment
 * (background image / gradient / border color / shadow) sits BEHIND it and
 * only shows through the portrait's transparent corners. The portrait's own
 * circular alpha clip produces a clean, edge-to-edge result — no nesting, no peek.
 */
export function FreeCompositeCard({ title, spec, href, section, cardId, toolId }: FreeCompositeCardProps) {
  const DEMO_PHOTO = demoPhotoFor(cardId);

  // Background treatment style for the card container
  const treatmentStyle: React.CSSProperties =
    spec.kind === "gradient" && spec.gradientCss
      ? { background: spec.gradientCss }
      : { background: "#e5e7eb" }; // neutral canvas for border/shadow/text

  // Border cards get their own layout: circle-cropped portrait + colored ring on a neutral canvas
  if (spec.kind === "border") {
    return (
      <Link
        href={href}
        onClick={() => fireClick(toolId, "style", cardId, section, href)}
        className="group block bg-white rounded-xl overflow-hidden hover:border-primary hover:shadow-lg transition-all border border-gray-100"
      >
        <div className="relative w-full aspect-square overflow-hidden bg-gray-100 flex items-center justify-center">
          {/* ring (outer colored disc) */}
          <div
            className="rounded-full w-[90%] aspect-square flex items-center justify-center group-hover:scale-105 transition-transform duration-300"
            style={{ background: spec.borderColor || "#1a1a1a" }}
          >
            {/* inner circle: complementary background + portrait cutout */}
            <div className="rounded-full overflow-hidden w-[86%] aspect-square relative">
              <div
                className="absolute inset-0"
                style={{ background: spec.borderBg || "#f3f4f6" }}
              />
              <img src={DEMO_PHOTO} alt={title} className="relative w-full h-full object-contain object-bottom" loading="lazy" />
            </div>
          </div>
        </div>
        <div className="p-2 md:p-3">
          <p className="font-semibold text-gray-800 text-xs md:text-sm leading-tight line-clamp-2">{title}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      href={href}
      onClick={() => fireClick(toolId, "style", cardId, section, href)}
      className="group block bg-white rounded-xl overflow-hidden hover:border-primary hover:shadow-lg transition-all border border-gray-100"
    >
      <div className="relative w-full aspect-square overflow-hidden" style={treatmentStyle}>
        {/* Background image treatment (fills card, behind portrait) */}
        {spec.kind === "background" && spec.backgroundUrl && (
          <img src={spec.backgroundUrl} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        )}

        {/* Full-bleed portrait — its own alpha cutout defines the shape */}
        <img
          src={DEMO_PHOTO}
          alt={title}
          className="relative w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />

        {/* Shadow layer ON TOP of the portrait (order: background → selfie → shadow) */}
        {spec.kind === "shadow" && spec.shadowUrl && (
          <img
            src={spec.shadowUrl}
            alt=""
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[100%] h-[100%] object-contain pointer-events-none"
            loading="lazy"
          />
        )}

        {/* Text overlay */}
        {spec.kind === "text" && spec.text && (
          <div className="absolute inset-x-0 bottom-0 flex justify-center pb-3 pointer-events-none">
            <span
              className="font-bold text-lg md:text-xl"
              style={{ color: spec.textColor || "#ffffff", textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}
            >
              {spec.text}
            </span>
          </div>
        )}
      </div>
      <div className="p-2 md:p-3">
        <p className="font-semibold text-gray-800 text-xs md:text-sm leading-tight line-clamp-2">{title}</p>
      </div>
    </Link>
  );
}
